<template>
	<div class="bg5 p2">
		<div class="xs-12 flex wrap xs-between">
			<div class="fw7 fs4 c6 lh125">{{ productName }}</div>
		</div>
		<!-- header -->
		<div class="xs-12 mt6 flex">
			<div class="xs-6 fs3 fw7">{{ t("key") }}</div>
			<div class="xs-2 fs3 fw7">{{ t("rate") }} %</div>
			<div class="xs-2 fs3 fw7">{{ t("insured_amount") }}</div>
			<div class="xs-2 fs3 fw7 ta2">{{ t("premium") }}</div>
		</div>
		<!-- list -->
		<div class="mt2 mb6">
			<div
				v-for="(value, i) in values"
				:key="i"
				class="flex xs-12 pb1 xs-middle"
			>
				<div class="xs-6 fs3">{{ value.name }}</div>

				<!-- rate -->
				<div class="xs-2 pr1">
					<core-form-input
						componentName="j-base-input-number"
						:name="`${i}-value`"
						type="float"
						size="small"
						v-model="value.rate"
						v-validate="'required'"
						:validationErrors="errors"
						aria-autocomplete="none"
						@input="debouncedFetchPrice"
					/>
				</div>
				<!-- value -->
				<div class="xs-2">
					<core-form-input
						componentName="j-base-input-number"
						:name="`${i}-rate`"
						type="float"
						size="small"
						v-validate="'required'"
						v-model="value.value"
						:validationErrors="errors"
						aria-autocomplete="none"
						@input="debouncedFetchPrice"
					/>
				</div>
				<div class="xs-2 flex xs-middle xs-center fs3">
					{{ parseFloat(((value.value * value.rate) / 100).toFixed(6)) }}
				</div>
			</div>
		</div>

		<div
			class="mb3 flex xs-around xs-middle"
			v-for="(item, i) in appliedTaxes"
			:key="i"
		>
			<div class="xs-6">
				<span class="fw7">{{ item.name }}:</span>
			</div>
			<div class="xs-6 flex xs-right">
				<span class="fw7">{{ item.value }}</span>
			</div>
		</div>
		<div class="flex xs-around xs-middle">
			<div class="xs-6">
				<span class="fw7">{{ t("total_amount") }}:</span>
			</div>
			<div class="xs-6 flex xs-right">
				<span class="fw7">{{ computedPrice }}</span>
			</div>
		</div>
		<!-- Save value -->
		<j-button class="mt6" size="small" @click="saveValues">
			{{ t("save_values") }}
		</j-button>
	</div>
</template>

<script>
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import debounce from "lodash.debounce"

import JButton from "@/components/JButton"
import CoreFormInput from "@/modules/core/components/CoreFormInput"

export default {
	props: {
		productId: {
			validator(value) {
				return typeof value === "object"
			}
		},
		caseId: String,
		proposalId: String,
		offerId: String
	},

	components: {
		CoreFormInput,
		JButton
	},

	data() {
		return {
			productName: "",
			price: null,
			values: null,
			isDisabled: null,
			appliedTaxes: null
		}
	},

	computed: {
		computedValues() {
			return this.values
		},
		computedPrice() {
			return this.price
		},
		computedTaxes() {
			return this.appliedTaxes
		},
		plan() {
			return this.productId?.plan
		}
	},

	methods: {
		t(key) {
			const locale = {
				"en-US": {
					key: "Key",
					rate: "Rate",
					premium: " Premium",
					save_values: "Save Values",
					total_amount: "Total Amount",
					retail: "Retail",
					wholesale: "Wholesale",
					value: "Value",
					insured_amount: "Insured Amount/Value"
				},
				"zh-HK": {}
			}
			return locale[this.$store.state.settings.locale][key]
		},

		async fetchPrice() {
			const isValid = await this.$validator.validate()
			if (!isValid) return

			const query = gql`
				query getPrice($where: productWhereInput, $values: [keyValueInput]) {
					products: products_2(where: $where, values: $values) {
						list {
							name
							productId {
								plan
							}
							pricing(values: $values) {
								appliedTaxes {
									name
									code
									ratio
									formattedFlat
								}
								formattedPrice
							}
						}
					}
				}
			`

			const res = await api({
				query,
				variables: {
					where: {
						productId: {
							plan: this.plan
						}
					},
					...this.massageValuesForAPI()
				}
			})

			this.productName = res?.data?.products?.list?.[0]?.name
			this.price = res?.data?.products?.list?.[0]?.pricing?.formattedPrice
			this.appliedTaxes = res?.data?.products?.list?.[0]?.pricing?.appliedTaxes.map(
				item => {
					return {
						name: item.name ? item.name : item.code,
						value: this.getValue(item).flat
					}
				}
			)
		},

		debouncedFetchPrice: debounce(function() {
			this.fetchPrice()
		}, 500),

		async fetchValues() {
			const query = gql`
				fragment ObjectValue on scalarValue {
					objectValue {
						key
						value {
							objectValue {
								key
								value {
									numberValue
									stringValue
								}
							}
						}
					}
				}

				query checkPolicyValues(
					$caseWhere1: caseWhere
					$offerWhere1: offerWhere
				) {
					cases(where: $caseWhere1) {
						list {
							id
							proposals {
								basket(where: $offerWhere1) {
									id
									values {
										key
										value {
											arrayValue {
												...ObjectValue
											}
										}
									}
								}
							}
						}
					}
				}
			`

			const res = await api({
				query,
				variables: {
					caseWhere1: { id: this.caseId },
					offerWhere1: { id: this.offerId }
				}
			})

			this.isDisabled = true

			this.values =
				this.massageValuesFromAPI(
					res?.data?.cases?.list?.[0]?.proposals?.[0]?.basket?.[0]?.values
				) || {}

			// Check if all insureds are in values, add them with empty rate and value if not
			const valuesIds = Object.values(this.values).map(value => value.id)
			this.$store.state.caseManagement.case.insureds.forEach(insured => {
				if (!valuesIds.includes(insured.id)) {
					this.values = {
						...this.values,
						[Object.keys(this.values).length]: {
							...insured,
							name: insured.name,
							value: 0,
							rate: 0
						}
					}
				}
			})
			// Check if an insured has been removed, and remove the corresponding value
			const insuredsIds = Object.values(
				this.$store.state.caseManagement.case.insureds
			).map(insured => insured.id)
			Object.keys(this.values).forEach(key => {
				if (!insuredsIds.includes(this.values[key]?.id)) delete this.values[key]
			})
		},

		async saveValues() {
			const query = gql`
				mutation updateOfferOfProposal(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$updateOfferInput: updateOfferInput!
				) {
					updateOfferOfProposal(
						caseId: $caseId
						offerId: $offerId
						proposalId: $proposalId
						updateOfferInput: $updateOfferInput
					) {
						errors
						errors_2 {
							code
							message
						}
						status
					}
				}
			`
			const variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				offerId: this.offerId,
				updateOfferInput: this.massageValuesForAPI()
			}

			const res = await api({
				query,
				variables
			})

			if (!res?.data?.updateOfferOfProposal?.errors)
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("success")
					}
				})
			this.isDisabled = true
			this.$emit("premium:updated")
		},

		massageValuesFromAPI(values) {
			const newValues = {}
			values
				?.find(item => item.key === "insureds")
				?.value?.arrayValue.forEach((item, i) => {
					let key = item?.objectValue[0]?.key // tour
					let value = item?.objectValue[0]?.value?.objectValue // array for 1 insured
					newValues[i] = {
						rate: parseFloat(
							(
								value.find(v => v.key === "rate")?.value?.numberValue * 100
							).toFixed(6)
						),
						name: value.find(v => v.key === "name")?.value?.stringValue,
						id: value.find(v => v.key === "id")?.value?.stringValue,
						value: value.find(v => v.key === "value")?.value?.numberValue
					}
				})
			return newValues
		},

		massageValuesForAPI() {
			return {
				values: [
					{
						key: "insureds",
						value: {
							arrayValue: Object.values(this.computedValues).map(
								computedValue => {
									return {
										objectValue: [
											{
												key: "exhibition",
												value: {
													objectValue: [
														{
															key: "rate",
															value: {
																numberValue: computedValue.rate / 100
															}
														},
														{
															key: "value",
															value: {
																numberValue: computedValue.value
															}
														},
														{
															key: "name",
															value: {
																stringValue: computedValue.name
															}
														},
														{
															key: "id",
															value: {
																stringValue: computedValue.id
															}
														}
													]
												}
											}
										]
									}
								}
							)
						}
					}
				]
			}
		},

		getValue(item) {
			if (!item) return ""
			const ratio = item.ratio ? item.ratio * 100 : null
			const flat = item.formattedFlat ? item.formattedFlat : "0"
			return {
				flat,
				summary: ratio
					? `(${ratio ? ratio.toFixed(2) : "0"}% of ${
							item.formattedOriginalPrice
					  })`
					: ""
			}
		}
	},

	async created() {
		await this.fetchValues()
		await this.fetchPrice()
	},

	watch: {
		// When productId updates, refetch data
		// This is need because we use same component for different productId
		// And thus we have to refetch as AegPricing doesn't remount the component
		productId: {
			handler() {
				this.fetchPrice()
			}
		},
		// when offerId changes,
		// and refetch values
		offerId: async function() {
			await this.fetchValues()
		}
	}
}
</script>
